import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  VStack, Input, Select, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  useToast, useDisclosure, Spinner, IconButton, Divider, Switch,
  Textarea, Tooltip, SimpleGrid, Tag, TagLabel, TagCloseButton, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiPlus, FiEdit2, FiTrash2, FiSend,
         FiCopy, FiCheck, FiX, FiCalendar, FiClock, FiUser } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = { draft:'gray', sent:'blue', confirmed:'green', rescheduled:'orange', cancelled:'red' };
const STATUS_LABELS = { draft:'Draft', sent:'Sent', confirmed:'Confirmed', rescheduled:'Rescheduled', cancelled:'Cancelled' };

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  const [y,m,day] = s.split('-');
  return `${day}/${m}/${y}`;
};
const fmtTime = (t) => t ? String(t).slice(0,5) : '';

// ── Create/Edit Modal ─────────────────────────────────────────────────────────
function AppointmentModal({ isOpen, onClose, onSaved, editAppt, currentUser, prefill }) {
  const BLANK = {
    title:'', appointment_type:'remote_video', premises_id:'', manual_location:'',
    video_link:'', project_id:'', task_id:'', sub_task_id:'',
    duration_minutes:60, duration_unit:'minutes',
    date_mode:'any_available', date_ranges:[], slot_start_time:'09:00', slot_end_time:'17:00',
    message:'', contact_ids:[], resource_ids:[], check_general_calendar:true,
  };
  const [form, setForm] = useState(BLANK);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [subTasks, setSubTasks] = useState([]);
  const [premises, setPremises] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companyContacts, setCompanyContacts] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourceTypeFilter, setResourceTypeFilter] = useState('all');
  const [companySearch, setCompanySearch] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ first_name: '', last_name: '', email: '', mobile: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dateRangeFrom, setDateRangeFrom] = useState('');
  const [dateRangeTo, setDateRangeTo] = useState('');
  // Password protection
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const toast = useToast();
  const set = (k,v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!isOpen) return;
    setForm(editAppt ? { ...BLANK, ...editAppt,
      contact_ids: (editAppt.contacts||[]).map(c=>c.contact_id),
      resource_ids: (editAppt.resources||[]).map(r=>r.resource_id),
      date_ranges: editAppt.date_ranges ? (typeof editAppt.date_ranges==='string' ? JSON.parse(editAppt.date_ranges) : editAppt.date_ranges) : [],
    } : { ...BLANK, ...(prefill || {}), contact_ids: prefill?.contact_ids || [] });
    // Seed password state
    setPasswordEnabled(!!(editAppt?.booking_password_hash));
    setPasswordValue('');
    setShowPasswordInput(false);
    // Seed knownContacts from any prefilled contact objects so names show immediately
    if (prefill?.prefill_contacts?.length) {
      const m = {};
      prefill.prefill_contacts.forEach(c => { m[c.id] = c; });
      setKnownContacts(m);
    } else {
      setKnownContacts({});
    }
    setCompanySearch(''); setCompanySuggestions([]); setSelectedCompany(null); setProjects([]);
    // If prefill has a company, load it and its projects
    const prefillCompanyId = !editAppt && prefill?.company_id;
    const prefillProjectId  = !editAppt && prefill?.project_id;
    const companyId = editAppt?.company_id || prefillCompanyId;
    if (companyId) {
      api.get(`/companies/${companyId}`)
        .then(r => { setSelectedCompany(r.data); setCompanySearch(r.data.name); return api.get(`/projects?company_id=${companyId}`); })
        .then(async r => {
          let list = r.data || [];
          // Ensure the prefilled project is always in the list (it may belong to a different company)
          if (prefillProjectId && !list.find(p => p.id === prefillProjectId)) {
            try {
              const { data: p } = await api.get(`/projects/${prefillProjectId}`);
              if (p && p.id) list = [p, ...list];
            } catch(e) {}
          }
          setProjects(list);
        }).catch(()=>{});
      // Also load contacts for this company
      api.get(`/contacts?company_id=${companyId}&limit=50`).then(r => {
        const list = r.data || [];
        setCompanyContacts(list);
        setKnownContacts(prev => { const m = {...prev}; list.forEach(c => { m[c.id] = c; }); return m; });
      }).catch(()=>{});
    } else if (prefillProjectId) {
      // No company but we have a project — load it directly
      api.get(`/projects/${prefillProjectId}`)
        .then(r => {
          const p = r.data;
          if (p) {
            setProjects([p]);
            if (p.company_id) {
              api.get(`/companies/${p.company_id}`).then(cr => { setSelectedCompany(cr.data); setCompanySearch(cr.data.name); }).catch(()=>{});
            }
          }
        }).catch(()=>{});
    }
    api.get('/visitors/premises/list').then(r => setPremises(r.data||[])).catch(()=>{});
    api.get('/resources').then(r => {
      const all = r.data || [];
      setResources(all);
      // Default: add the logged-in user's person resource if not editing
      if (!editAppt && currentUser) {
        const userRes = all.find(res => res.user_id && res.user_id === currentUser.id);
        if (userRes && !form.resource_ids.includes(userRes.id)) {
          setForm(p => ({ ...p, resource_ids: [userRes.id] }));
        }
      }
    }).catch(()=>{});
  }, [isOpen, editAppt]); // eslint-disable-line

  useEffect(() => {
    if (form.project_id) {
      api.get(`/tasks?project_id=${form.project_id}`).then(r => setTasks(r.data||[])).catch(()=>{});
    } else setTasks([]);
    setSubTasks([]);
  }, [form.project_id]);

  useEffect(() => {
    if (form.task_id) {
      api.get(`/sub-tasks?task_id=${form.task_id}`).then(r => setSubTasks(r.data||[])).catch(()=>{});
    } else setSubTasks([]);
  }, [form.task_id]);

  const searchCompanies = async (q) => {
    if (!q || q.length < 2) { setCompanySuggestions([]); return; }
    try {
      const { data } = await api.get(`/companies?search=${encodeURIComponent(q)}&limit=10`);
      setCompanySuggestions(data || []);
    } catch(e) { setCompanySuggestions([]); }
  };

  const selectCompany = async (company) => {
    setSelectedCompany(company);
    setCompanySearch(company.name);
    setCompanySuggestions([]);
    set('project_id', ''); set('task_id', ''); set('sub_task_id', '');
    setTasks([]); setSubTasks([]);
    try {
      const { data } = await api.get(`/projects?company_id=${company.id}`);
      setProjects(data || []);
    } catch(e) { setProjects([]); }
    try {
      const { data } = await api.get(`/contacts?company_id=${company.id}&limit=50`);
      setCompanyContacts(data || []);
    } catch(e) { setCompanyContacts([]); }
  };

  const clearCompany = () => {
    setSelectedCompany(null); setCompanySearch(''); setCompanySuggestions([]);
    setProjects([]); setTasks([]); setSubTasks([]); setCompanyContacts([]);
    set('project_id', ''); set('task_id', ''); set('sub_task_id', '');
  };

  const searchContacts = async () => {
    if (!contactSearch) return;
    const { data } = await api.get(`/contacts?search=${encodeURIComponent(contactSearch)}&limit=20`).catch(()=>({data:[]}));
    setContacts(data);
  };

  const saveNewContact = async () => {
    if (!newContactForm.first_name) return;
    setSavingContact(true);
    try {
      const payload = { ...newContactForm, company_id: selectedCompany?.id || null };
      const { data } = await api.post('/contacts', payload);
      addContact(data);
      if (selectedCompany) setCompanyContacts(prev => [...prev, data]);
      setNewContactForm({ first_name: '', last_name: '', email: '', mobile: '' });
      setShowAddContactForm(false);
      toast({ title: 'Contact added', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed to create contact', status: 'error', duration: 2000 }); }
    setSavingContact(false);
  };

  // Keep a map of id -> contact object so tags can display names
  const [knownContacts, setKnownContacts] = useState({});

  const addContact = (c) => {
    if (!form.contact_ids.includes(c.id)) set('contact_ids', [...form.contact_ids, c.id]);
    setKnownContacts(prev => ({ ...prev, [c.id]: c }));
    setContacts([]); setContactSearch('');
  };

  const removeContact = (id) => set('contact_ids', form.contact_ids.filter(x => x !== id));

  const addDateRange = () => {
    if (!dateRangeFrom) return;
    set('date_ranges', [...(form.date_ranges||[]), { from: dateRangeFrom, to: dateRangeTo || dateRangeFrom }]);
    setDateRangeFrom(''); setDateRangeTo('');
  };

  const getDurationMinutes = () => {
    if (form.duration_unit === 'hours') return (form.duration_minutes || 1) * 60;
    if (form.duration_unit === 'day') return 480;
    return form.duration_minutes || 60;
  };

  const save = async () => {
    if (!form.project_id) { toast({ title: 'Please select a project', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = { ...form, duration_minutes: getDurationMinutes() };
      let savedId = editAppt?.id;
      if (editAppt) await api.put(`/appointments/${editAppt.id}`, payload);
      else { const { data } = await api.post('/appointments', payload); savedId = data.id; }
      // Set password on new appointments if one was entered
      if (!editAppt && savedId && passwordEnabled && passwordValue.trim()) {
        await api.patch(`/appointments/${savedId}/password`, { password: passwordValue }).catch(() => {});
      }
      toast({ title: 'Saved', status: 'success', duration: 1500 });
      onSaved(); onClose();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">{editAppt ? 'Edit Appointment Request' : 'New Appointment Request'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl><FormLabel fontSize="11px">Title (optional — auto-generated if blank)</FormLabel>
              <Input size="sm" value={form.title} onChange={e=>set('title',e.target.value)} placeholder={`Meeting with ${currentUser?.display_name || 'you'}`} /></FormControl>

            <FormControl isRequired><FormLabel fontSize="11px">Appointment Type</FormLabel>
              <Select size="sm" value={form.appointment_type} onChange={e=>set('appointment_type',e.target.value)}>
                <option value="remote_video">📹 Remote video call</option>
                <option value="in_person_visit_us">🤝 In person — we come to you</option>
                <option value="in_person_visit_them">🏢 In person — invite to our premises</option>
              </Select>
            </FormControl>

            {form.appointment_type === 'in_person_visit_them' && (
              <HStack align="flex-start">
                <FormControl flex={2}><FormLabel fontSize="11px">Premises</FormLabel>
                  <Select size="sm" value={form.premises_id} onChange={e=>set('premises_id',e.target.value)}>
                    <option value="">Select premises...</option>
                    {premises.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </FormControl>
                <FormControl flex={2}><FormLabel fontSize="11px">Or manual location</FormLabel>
                  <Input size="sm" value={form.manual_location} onChange={e=>set('manual_location',e.target.value)} placeholder="Address or place name" /></FormControl>
              </HStack>
            )}
            {form.appointment_type === 'remote_video' && (
              <FormControl><FormLabel fontSize="11px">Video call link (optional)</FormLabel>
                <Input size="sm" value={form.video_link} onChange={e=>set('video_link',e.target.value)} placeholder="https://meet.google.com/..." /></FormControl>
            )}

            <Divider />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.500">Project Link</Text>

            {/* Company search */}
            <FormControl isRequired>
              <FormLabel fontSize="11px">Company</FormLabel>
              <Box position="relative">
                <HStack>
                  <Input size="sm" placeholder="Search by company name..."
                    value={companySearch}
                    onChange={e => { setCompanySearch(e.target.value); searchCompanies(e.target.value); }}
                    isReadOnly={!!selectedCompany}
                    bg={selectedCompany ? 'green.50' : 'white'}
                    borderColor={selectedCompany ? 'green.300' : 'inherit'}
                    flex={1} />
                  {selectedCompany && (
                    <IconButton size="sm" icon={<FiX size={12}/>} variant="ghost" colorScheme="red"
                      onClick={clearCompany} aria-label="Clear company" />
                  )}
                </HStack>
                {companySuggestions.length > 0 && (
                  <Box position="absolute" top="100%" left={0} right={0} bg="white"
                    border="1px" borderColor="gray.200" borderRadius="md" shadow="md" zIndex={10} maxH="180px" overflowY="auto">
                    {companySuggestions.map(c => (
                      <Box key={c.id} px={3} py={2} cursor="pointer" fontSize="12px"
                        _hover={{ bg: 'gray.50' }} onClick={() => selectCompany(c)}>
                        <Text fontWeight="600">{c.name}</Text>
                        {c.company_type && <Text fontSize="10px" color="gray.400" textTransform="capitalize">{c.company_type}</Text>}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </FormControl>

            {/* Project — only shown once company is selected */}
            {selectedCompany && (
              <FormControl isRequired>
                <FormLabel fontSize="11px">Project</FormLabel>
                {projects.length === 0
                  ? <Text fontSize="12px" color="gray.400" fontStyle="italic">No projects found for {selectedCompany.name}</Text>
                  : <Select size="sm" value={form.project_id} onChange={e=>{set('project_id',e.target.value);set('task_id','');set('sub_task_id','');}}>
                      <option value="">Select project...</option>
                      {projects.map(p=><option key={p.id} value={p.id}>{p.project_name}</option>)}
                    </Select>
                }
              </FormControl>
            )}

            {/* Task — shown once project selected */}
            {form.project_id && (
              <FormControl>
                <FormLabel fontSize="11px">Task (optional)</FormLabel>
                <Select size="sm" value={form.task_id} onChange={e=>{set('task_id',e.target.value);set('sub_task_id','');}}>
                  <option value="">— No specific task —</option>
                  {tasks.map(t=><option key={t.id} value={t.id}>{t.task_title}</option>)}
                </Select>
              </FormControl>
            )}

            {/* Sub-task — shown if task selected and sub-tasks exist */}
            {form.task_id && subTasks.length > 0 && (
              <FormControl>
                <FormLabel fontSize="11px">Sub-task (optional)</FormLabel>
                <Select size="sm" value={form.sub_task_id} onChange={e=>set('sub_task_id',e.target.value)}>
                  <option value="">— None —</option>
                  {subTasks.map(s=><option key={s.id} value={s.id}>{s.title}</option>)}
                </Select>
              </FormControl>
            )}

            <Divider />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.500">Duration & Availability</Text>
            <HStack>
              <FormControl flex={1}>
                <FormLabel fontSize="11px">Duration</FormLabel>
                <HStack>
                  {form.duration_unit !== 'day' && <Input size="sm" type="number" min={1} w="70px" value={form.duration_minutes} onChange={e=>set('duration_minutes',parseInt(e.target.value)||1)} />}
                  <Select size="sm" value={form.duration_unit||'minutes'} onChange={e=>set('duration_unit',e.target.value)}>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="day">Block the day</option>
                  </Select>
                </HStack>
              </FormControl>
              <FormControl flex={1}><FormLabel fontSize="11px">Available window</FormLabel>
                <HStack><Input size="sm" type="time" value={form.slot_start_time||'09:00'} onChange={e=>set('slot_start_time',e.target.value)} w="110px" />
                <Text fontSize="11px" color="gray.400">to</Text>
                <Input size="sm" type="time" value={form.slot_end_time||'17:00'} onChange={e=>set('slot_end_time',e.target.value)} w="110px" /></HStack>
              </FormControl>
            </HStack>
            <FormControl><FormLabel fontSize="11px">Date availability</FormLabel>
              <Select size="sm" value={form.date_mode} onChange={e=>set('date_mode',e.target.value)}>
                <option value="any_available">Any available date (next 90 days, Mon–Fri)</option>
                <option value="specific_dates">Specific dates / date ranges</option>
              </Select>
            </FormControl>
            {form.date_mode === 'specific_dates' && (
              <Box>
                <HStack mb={2}>
                  <Input size="xs" type="date" value={dateRangeFrom} onChange={e=>setDateRangeFrom(e.target.value)} w="140px" />
                  <Text fontSize="11px" color="gray.400">to</Text>
                  <Input size="xs" type="date" value={dateRangeTo} onChange={e=>setDateRangeTo(e.target.value)} w="140px" />
                  <Button size="xs" colorScheme="orange" onClick={addDateRange} leftIcon={<FiPlus size={10}/>}>Add</Button>
                </HStack>
                <VStack spacing={1} align="stretch">
                  {(form.date_ranges||[]).map((r,i) => (
                    <HStack key={i} bg="orange.50" px={2} py={1} borderRadius="md" fontSize="12px">
                      <Text flex={1}>{r.from === r.to ? r.from : `${r.from} — ${r.to}`}</Text>
                      <IconButton icon={<FiX size={10}/>} size="xs" variant="ghost" colorScheme="red"
                        onClick={() => set('date_ranges', form.date_ranges.filter((_,j)=>j!==i))} aria-label="Remove" />
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            <Divider />
            {/* Availability checks */}
            <Box bg="gray.50" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
              <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.500" mb={2}>Availability Checks</Text>

              {/* General calendar toggle */}
              <HStack justify="space-between" mb={3} p={2} bg={form.check_general_calendar ? 'blue.50' : 'white'}
                border="1px" borderColor={form.check_general_calendar ? 'blue.200' : 'gray.200'} borderRadius="md">
                <Box>
                  <Text fontSize="12px" fontWeight="600">General Calendar</Text>
                  <Text fontSize="10px" color="gray.500">Any calendar entry on a date marks it unavailable</Text>
                </Box>
                <Switch size="sm" colorScheme="blue" isChecked={!!form.check_general_calendar}
                  onChange={e => set('check_general_calendar', e.target.checked)} />
              </HStack>

              {/* Resource type filter */}
              <Text fontSize="11px" fontWeight="600" color="gray.600" mb={2}>Add Resources to Check</Text>
              <HStack mb={2} spacing={1}>
                {['all','Person','Room','Asset'].map(type => (
                  <Button key={type} size="xs" borderRadius="full"
                    variant={resourceTypeFilter === type ? 'solid' : 'outline'}
                    colorScheme={resourceTypeFilter === type ? 'teal' : 'gray'}
                    onClick={() => setResourceTypeFilter(type)}>
                    {type === 'all' ? 'All' : type === 'Person' ? '👤 Person' : type === 'Room' ? '🏠 Room' : '📦 Asset'}
                  </Button>
                ))}
              </HStack>

              {/* Resource selector */}
              <Select size="sm" mb={2} onChange={e => {
                if (e.target.value && !form.resource_ids.includes(e.target.value))
                  set('resource_ids', [...form.resource_ids, e.target.value]);
                e.target.value = '';
              }}>
                <option value="">Add resource...</option>
                {resources
                  .filter(r => !form.resource_ids.includes(r.id))
                  .filter(r => resourceTypeFilter === 'all' || r.type_name === resourceTypeFilter || r.resource_type_id === {'Person':3,'Room':1,'Asset':2}[resourceTypeFilter])
                  .map(r => <option key={r.id} value={r.id}>{r.name}{r.type_name ? ` (${r.type_name})` : ''}</option>)}
              </Select>

              {/* Selected resources */}
              <Wrap spacing={1}>
                {form.resource_ids.map(rid => {
                  const r = resources.find(x => x.id === rid);
                  if (!r) return null;
                  const isUserRes = currentUser && r.user_id === currentUser.id;
                  const typeColor = r.resource_type_id === 3 ? 'purple' : r.resource_type_id === 1 ? 'blue' : 'orange';
                  return (
                    <Tag key={rid} size="sm" colorScheme={typeColor} variant={isUserRes ? 'solid' : 'subtle'}>
                      <TagLabel>
                        {r.resource_type_id === 3 ? '👤' : r.resource_type_id === 1 ? '🏠' : '📦'} {r.name}
                        {isUserRes && ' (you)'}
                      </TagLabel>
                      <TagCloseButton onClick={() => set('resource_ids', form.resource_ids.filter(x => x !== rid))} />
                    </Tag>
                  );
                })}
                {form.resource_ids.length === 0 && (
                  <Text fontSize="11px" color="gray.400" fontStyle="italic">No resources selected — only calendar will be checked if enabled above</Text>
                )}
              </Wrap>
            </Box>

            <Divider />
            <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color="gray.500">Contacts to invite</Text>

            {/* Company contacts listed first */}
            {companyContacts.length > 0 && (
              <Box>
                <Text fontSize="10px" color="gray.400" mb={1}>{selectedCompany ? `${selectedCompany.name} contacts:` : 'Company contacts:'}</Text>
                <Wrap spacing={1}>
                  {companyContacts.map(c => {
                    const already = form.contact_ids.includes(c.id);
                    return (
                      <WrapItem key={c.id}>
                        <Badge colorScheme={already ? 'purple' : 'gray'} variant={already ? 'solid' : 'outline'}
                          cursor={already ? 'default' : 'pointer'} fontSize="10px" px={2} py={0.5} borderRadius="full"
                          onClick={() => !already && addContact(c)}>
                          {c.first_name} {c.last_name || ''}{already && <span style={{marginLeft:'4px',opacity:0.7}}>✓</span>}
                        </Badge>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              </Box>
            )}

            {/* Search all contacts */}
            <HStack>
              <Input size="sm" placeholder="Search all contacts by name or email..." value={contactSearch}
                onChange={e => setContactSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchContacts()} flex={1} />
              <Button size="sm" variant="ghost" onClick={searchContacts}>Search</Button>
              <Button size="sm" variant="ghost" colorScheme="green" onClick={() => setShowAddContactForm(v => !v)}>
                {showAddContactForm ? 'Cancel' : '+ New'}
              </Button>
            </HStack>

            {contacts.length > 0 && (
              <Box border="1px" borderColor="gray.200" borderRadius="md" maxH="150px" overflowY="auto">
                {contacts.map(c => (
                  <Box key={c.id} px={3} py={2} cursor="pointer" _hover={{bg:'gray.50'}}
                    fontSize="12px" onClick={() => addContact(c)}>
                    <b>{c.first_name} {c.last_name}</b> {c.email && `— ${c.email}`}
                    {!c.email && <Badge colorScheme="orange" fontSize="8px" ml={1}>No email</Badge>}
                  </Box>
                ))}
              </Box>
            )}

            {/* Add new contact inline */}
            {showAddContactForm && (
              <Box p={3} border="1px" borderColor="green.200" borderRadius="md" bg="green.50">
                <Text fontSize="11px" fontWeight="700" color="green.700" mb={2}>
                  New contact{selectedCompany ? ` for ${selectedCompany.name}` : ''}
                </Text>
                <SimpleGrid columns={2} spacing={2} mb={2}>
                  <Input size="sm" placeholder="First name *" value={newContactForm.first_name}
                    onChange={e => setNewContactForm(p => ({...p, first_name: e.target.value}))} />
                  <Input size="sm" placeholder="Last name" value={newContactForm.last_name}
                    onChange={e => setNewContactForm(p => ({...p, last_name: e.target.value}))} />
                  <Input size="sm" placeholder="Email" value={newContactForm.email}
                    onChange={e => setNewContactForm(p => ({...p, email: e.target.value}))} />
                  <Input size="sm" placeholder="Mobile" value={newContactForm.mobile}
                    onChange={e => setNewContactForm(p => ({...p, mobile: e.target.value}))} />
                </SimpleGrid>
                <Flex justify="flex-end">
                  <Button size="sm" colorScheme="green" isLoading={savingContact} onClick={saveNewContact}>
                    Save &amp; Add to Invite
                  </Button>
                </Flex>
              </Box>
            )}

            <Wrap>
              {form.contact_ids.map(cid => {
                const found = knownContacts[cid] || companyContacts.find(c => c.id === cid) || contacts.find(c => c.id === cid);
                return (
                  <Tag key={cid} size="sm" colorScheme="purple">
                    <TagLabel>{found ? `${found.first_name} ${found.last_name||''}` : `Contact #${cid}`}</TagLabel>
                    <TagCloseButton onClick={() => removeContact(cid)} />
                  </Tag>
                );
              })}
            </Wrap>

            <Divider />
            <FormControl>
              <FormLabel fontSize="11px">Personal message (included in email)</FormLabel>
              <Textarea size="sm" rows={4} value={form.message} onChange={e=>set('message',e.target.value)}
                placeholder="Hi, I'd like to schedule a meeting to discuss..." />
            </FormControl>

            <Divider />

            {/* Password protection */}
            <Box w="100%" p={3} border="1px" borderColor={passwordEnabled ? 'orange.200' : 'gray.100'} borderRadius="md" bg={passwordEnabled ? 'orange.50' : 'gray.50'}>
              <Flex justify="space-between" align="center" mb={passwordEnabled ? 2 : 0}>
                <Box>
                  <Text fontSize="12px" fontWeight="700" color={passwordEnabled ? 'orange.700' : 'gray.600'}>
                    🔒 Password Protection
                  </Text>
                  <Text fontSize="10px" color="gray.400">Require a password before the booking form is accessible</Text>
                </Box>
                <Switch size="sm" colorScheme="orange" isChecked={passwordEnabled}
                  onChange={async e => {
                    const enabling = e.target.checked;
                    setPasswordEnabled(enabling);
                    if (!enabling && editAppt?.id) {
                      // Clear immediately when toggled off
                      await api.patch(`/appointments/${editAppt.id}/password`, { password: '' }).catch(() => {});
                      toast({ title: 'Password removed', status: 'info', duration: 1500 });
                    }
                    if (enabling) setShowPasswordInput(true);
                    else setShowPasswordInput(false);
                  }} />
              </Flex>
              {passwordEnabled && (
                <Box mt={2}>
                  {!showPasswordInput && editAppt?.id ? (
                    <HStack spacing={2}>
                      <Text fontSize="11px" color="orange.600">Password is set.</Text>
                      <Button size="xs" variant="outline" colorScheme="orange"
                        onClick={() => { setPasswordValue(''); setShowPasswordInput(true); }}>
                        Change Password
                      </Button>
                    </HStack>
                  ) : (
                    <HStack spacing={2}>
                      <Input size="sm" type="password" placeholder="Enter password…" bg="white"
                        value={passwordValue} onChange={e => setPasswordValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && e.preventDefault()}
                        flex={1} autoFocus />
                      {editAppt?.id && (
                        <Button size="sm" colorScheme="orange" isLoading={savingPassword}
                          isDisabled={!passwordValue.trim()}
                          onClick={async () => {
                            if (!passwordValue.trim()) return;
                            setSavingPassword(true);
                            await api.patch(`/appointments/${editAppt.id}/password`, { password: passwordValue }).catch(() => {});
                            setSavingPassword(false);
                            setShowPasswordInput(false);
                            setPasswordValue('');
                            toast({ title: 'Password saved', status: 'success', duration: 1500 });
                          }}>
                          Set
                        </Button>
                      )}
                      {showPasswordInput && editAppt?.id && (
                        <Button size="sm" variant="ghost" onClick={() => { setShowPasswordInput(false); setPasswordValue(''); }}>
                          Cancel
                        </Button>
                      )}
                    </HStack>
                  )}
                  <Text fontSize="10px" color="orange.400" mt={1}>
                    {!editAppt?.id ? 'Password will be set after saving the request.' : ''}
                  </Text>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="orange" onClick={save} isLoading={saving}>
            {editAppt ? 'Save Changes' : 'Create Request'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function BookMyDiaryDashboard({ onBack }) {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAppt, setEditAppt] = useState(null);
  const [filter, setFilter] = useState('all');
  const createModal = useDisclosure();
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/appointments'); setAppts(data||[]); } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const send = async (id) => {
    try {
      const { data } = await api.post(`/appointments/${id}/send`);
      toast({ title: `Sent to ${data.sent} contact${data.sent!==1?'s':''}`, status: 'success', duration: 2000 });
      fetchAll();
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed to send', status: 'error', duration: 3000 }); }
  };

  const copyLink = async (id) => {
    try {
      const { data } = await api.get(`/appointments/${id}/link`);
      navigator.clipboard.writeText(data.url);
      toast({ title: 'Link copied to clipboard', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
  };

  const cancel = async (id) => {
    try {
      await api.put(`/appointments/${id}`, { status: 'cancelled' });
      toast({ title: 'Cancelled', status: 'info', duration: 1500 });
      fetchAll();
    } catch(e) {}
  };

  const markAttended = async (id) => {
    try {
      await api.put(`/appointments/${id}`, { attended: true });
      toast({ title: 'Marked as attended', status: 'success', duration: 1500 });
      fetchAll();
    } catch(e) {}
  };

  const del = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      fetchAll();
    } catch(e) {}
  };

  const filtered = filter === 'all' ? appts : appts.filter(a => a.status === filter);

  const counts = appts.reduce((acc, a) => { acc[a.status] = (acc[a.status]||0)+1; return acc; }, {});

  return (
    <Box p={4} maxW="1400px" mx="auto">
      <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={onBack}>Back</Button>
          <FiCalendar size={18} />
          <Text fontWeight="700" fontSize="16px">Book My Diary</Text>
        </HStack>
        <HStack>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={fetchAll}>Refresh</Button>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="orange" onClick={() => { setEditAppt(null); createModal.onOpen(); }}>
            New Request
          </Button>
        </HStack>
      </Flex>

      {/* Status filter pills */}
      <HStack mb={4} flexWrap="wrap" gap={2}>
        {['all','draft','sent','confirmed','rescheduled','cancelled'].map(s => (
          <Button key={s} size="xs" borderRadius="full"
            variant={filter===s ? 'solid' : 'outline'}
            colorScheme={filter===s ? 'orange' : 'gray'}
            onClick={() => setFilter(s)}>
            {s === 'all' ? `All (${appts.length})` : `${STATUS_LABELS[s]} (${counts[s]||0})`}
          </Button>
        ))}
      </HStack>

      {loading ? <Flex justify="center" py={10}><Spinner /></Flex> : (
        filtered.length === 0 ? (
          <Box textAlign="center" py={12} color="gray.400">
            <FiCalendar size={40} style={{ margin:'0 auto 12px' }} />
            <Text>No appointment requests yet</Text>
            <Button size="sm" mt={4} colorScheme="orange" onClick={() => { setEditAppt(null); createModal.onOpen(); }}>
              Create your first request
            </Button>
          </Box>
        ) : (
          <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="9px">Title</Th>
                  <Th fontSize="9px">Type</Th>
                  <Th fontSize="9px">Project / Task</Th>
                  <Th fontSize="9px">Contacts</Th>
                  <Th fontSize="9px">Confirmed Date</Th>
                  <Th fontSize="9px">Status</Th>
                  <Th fontSize="9px">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map(a => (
                  <Tr key={a.id} bg={a.attended ? 'green.50' : 'white'} opacity={a.status==='cancelled'?0.6:1}>
                    <Td>
                      <Text fontSize="12px" fontWeight="600">{a.title || `Meeting — ${a.user_name||user?.display_name}`}</Text>
                      {a.email_sent_at && <Text fontSize="10px" color="gray.400">Sent {fmtDate(a.email_sent_at)}</Text>}
                    </Td>
                    <Td fontSize="11px">
                      {a.appointment_type==='remote_video' ? '📹 Remote' : a.appointment_type==='in_person_visit_us' ? '🤝 Visit us' : '🏢 Our premises'}
                    </Td>
                    <Td>
                      <Text fontSize="11px" fontWeight="500">{a.project_name||'—'}</Text>
                      {a.task_title && <Text fontSize="10px" color="gray.400">{a.task_title}</Text>}
                    </Td>
                    <Td fontSize="11px" color="gray.500">{a.contact_count||0} contact{a.contact_count!==1?'s':''}</Td>
                    <Td>
                      {a.confirmed_date ? (
                        <Box>
                          <Text fontSize="12px" fontWeight="600" color="green.700">{fmtDate(a.confirmed_date)}</Text>
                          <Text fontSize="10px" color="green.500">{fmtTime(a.confirmed_start_time)}{a.confirmed_end_time?` – ${fmtTime(a.confirmed_end_time)}`:''}</Text>
                          {a.confirmed_by_name && <Text fontSize="10px" color="gray.400">by {a.confirmed_by_name}</Text>}
                        </Box>
                      ) : <Text fontSize="11px" color="gray.300">Not yet booked</Text>}
                    </Td>
                    <Td>
                      <Badge colorScheme={STATUS_COLORS[a.status]} fontSize="9px">{STATUS_LABELS[a.status]}</Badge>
                      {a.attended && <Badge colorScheme="teal" fontSize="9px" ml={1}>Attended</Badge>}
                    </Td>
                    <Td>
                      <HStack spacing={0}>
                        {['draft','rescheduled'].includes(a.status) && (
                          <Tooltip label="Send emails"><IconButton icon={<FiSend size={11}/>} size="xs" variant="ghost" colorScheme="blue" onClick={()=>send(a.id)} aria-label="Send" /></Tooltip>
                        )}
                        <Tooltip label="Copy booking link"><IconButton icon={<FiCopy size={11}/>} size="xs" variant="ghost" onClick={()=>copyLink(a.id)} aria-label="Copy link" /></Tooltip>
                        {a.status==='confirmed' && !a.attended && (
                          <Tooltip label="Mark as attended"><IconButton icon={<FiCheck size={11}/>} size="xs" variant="ghost" colorScheme="green" onClick={()=>markAttended(a.id)} aria-label="Mark attended" /></Tooltip>
                        )}
                        {['draft','sent','rescheduled'].includes(a.status) && (
                          <Tooltip label="Edit"><IconButton icon={<FiEdit2 size={11}/>} size="xs" variant="ghost" onClick={async ()=>{ const {data}=await api.get(`/appointments/${a.id}`); setEditAppt(data); createModal.onOpen(); }} aria-label="Edit" /></Tooltip>
                        )}
                        {!['cancelled'].includes(a.status) && (
                          <Tooltip label="Cancel"><IconButton icon={<FiX size={11}/>} size="xs" variant="ghost" colorScheme="red" onClick={()=>cancel(a.id)} aria-label="Cancel" /></Tooltip>
                        )}
                        <Tooltip label="Delete"><IconButton icon={<FiTrash2 size={11}/>} size="xs" variant="ghost" colorScheme="red" onClick={()=>del(a.id)} aria-label="Delete" /></Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )
      )}

      <AppointmentModal isOpen={createModal.isOpen} onClose={createModal.onClose}
        onSaved={fetchAll} editAppt={editAppt} currentUser={user} />
    </Box>
  );
}

export { AppointmentModal };
